import {NodeKey} from 'lexical';
import {useCallback, useEffect, useRef} from 'react';

type TableInfo = {
  tableElement: HTMLElement;
  tableKey: NodeKey;
  enableAddRows: boolean;
  enableAddColumns: boolean;
};

type DragState = {
  isDragging: boolean;
  startX: number;
  startY: number;
  lastThreshold: number;
  type: 'row' | 'column' | null;
};

/**
 * Handles drag-to-add functionality for rows and columns
 */
export function useTableDragToAdd(
  tableInfo: TableInfo | null,
  handleAddRow: () => void,
  handleAddColumn: () => void
) {
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    lastThreshold: 0,
    type: null,
  });

  const handleAddColumnDragStart = useCallback((e: React.MouseEvent) => {
    dragStateRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      lastThreshold: 0,
      type: 'column',
    };
  }, []);

  const handleAddRowDragStart = useCallback((e: React.MouseEvent) => {
    dragStateRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      lastThreshold: 0,
      type: 'row',
    };
  }, []);

  // Handle mouse move and mouse up for drag to add rows/columns
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState.isDragging || !tableInfo) return;

      const THRESHOLD = 40; // pixels per row/column

      if (dragState.type === 'column') {
        // Calculate horizontal distance
        const deltaX = e.clientX - dragState.startX;
        if (deltaX > 0) {
          const currentThreshold = Math.floor(deltaX / THRESHOLD);
          if (currentThreshold > dragState.lastThreshold) {
            // Add a column
            handleAddColumn();
            dragState.lastThreshold = currentThreshold;
          }
        }
      } else if (dragState.type === 'row') {
        // Calculate vertical distance
        const deltaY = e.clientY - dragState.startY;
        if (deltaY > 0) {
          const currentThreshold = Math.floor(deltaY / THRESHOLD);
          if (currentThreshold > dragState.lastThreshold) {
            // Add a row
            handleAddRow();
            dragState.lastThreshold = currentThreshold;
          }
        }
      }
    };

    const handleMouseUp = () => {
      dragStateRef.current = {
        isDragging: false,
        startX: 0,
        startY: 0,
        lastThreshold: 0,
        type: null,
      };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [tableInfo, handleAddColumn, handleAddRow]);

  return {
    handleAddColumnDragStart,
    handleAddRowDragStart,
  };
}
